import React, { useState, useEffect, useMemo } from 'react';
import { FloatingCalendarNav } from './FloatingCalendarNav';
import {
    Box,
    VStack,
    HStack,
    Stack,
    Text,
    Card,
    CardBody,
    Badge,
    Button,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Table,
    TableContainer,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Spinner,
    IconButton,
    Textarea,
    Checkbox,
    Divider,
    useColorMode,
    SimpleGrid,
} from "@chakra-ui/react";
import { Tooltip } from "./components/Tip";
import { FiPlus, FiTrash2, FiCalendar, FiUser, FiEdit, FiEye, FiMail, FiCopy, FiShare2, FiUserCheck, FiClock } from 'react-icons/fi';
import { SearchIcon, TriangleUpIcon, TriangleDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useQuery, useMutation } from '@apollo/client';
import { format } from 'date-fns';
import { getColor } from "../../brandConfig";
import { usePageTitle } from "../../hooks/useDocumentTitle";
import { useNavigate } from 'react-router-dom';
import ExternalCalendarModal from "./ExternalCalendarModal";
import ShareCalendarModal from "./components/ShareCalendarModal";
import { CalendarType, CalendarVisibility } from "../../generated/graphql";
import { PoweredBy } from "./components/PoweredBy";
import {
  GET_ALL_CALENDARS,
  GET_EXTERNAL_CALENDARS_ADMIN as GET_EXTERNAL_CALENDARS,
  CREATE_CALENDAR_ADMIN as CREATE_CALENDAR,
  DELETE_CALENDAR,
  UPDATE_CALENDAR_ADMIN as UPDATE_CALENDAR,
  GET_CLIENTS_ADMIN as GET_CLIENTS,
  TRANSFER_OWNERSHIP,
} from './queries';

// Sortable table columns (client-side sort over the loaded calendar list)
type CalendarSortField = 'name' | 'type' | 'visibility' | 'owner' | 'events' | 'createdAt';

// Filters/sort/page-size persisted per-browser, mirroring the employees
// page's "employeesListFilters" approach (module-local — no employees imports).
const CALENDAR_ADMIN_FILTERS_KEY = 'calendarAdminFilters';

const getSavedFilters = (): {
  searchTerm?: string;
  filterType?: string;
  filterVisibility?: string;
  sortBy?: CalendarSortField;
  sortOrder?: 'asc' | 'desc';
  pageSize?: number;
} => {
  try {
    const saved = localStorage.getItem(CALENDAR_ADMIN_FILTERS_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
};

const CalendarAccountsAdmin: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  // Calendar sharing is now managed via Email module (EmailAddress.linkedCalendars)
  const { isOpen: isTransferOpen, onOpen: onTransferOpen, onClose: onTransferClose } = useDisclosure();
  const { isOpen: isExternalOpen, onOpen: onExternalOpen, onClose: onExternalClose } = useDisclosure();
  const { isOpen: isShareOpen, onOpen: onShareOpen, onClose: onShareClose } = useDisclosure();

  const savedFilters = useMemo(getSavedFilters, []);

  const [selectedCalendar, setSelectedCalendar] = useState<any>(null);
  // Calendar selected for the Share modal (kept separate from selectedCalendar
  // so an open Edit/Transfer modal can't be clobbered)
  const [shareCalendar, setShareCalendar] = useState<any>(null);
  const [newOwnerId, setNewOwnerId] = useState('');
  const [searchTerm, setSearchTerm] = useState(savedFilters.searchTerm || '');
  const [debouncedSearch, setDebouncedSearch] = useState(savedFilters.searchTerm || '');
  const [filterType, setFilterType] = useState(savedFilters.filterType || 'all');
  const [filterVisibility, setFilterVisibility] = useState(savedFilters.filterVisibility || 'all');
  const [sortBy, setSortBy] = useState<CalendarSortField>(savedFilters.sortBy || 'createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(savedFilters.sortOrder || 'desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(savedFilters.pageSize || 10);
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<Set<string>>(new Set());

  // Debounce the search input (300ms) so filtering doesn't run per keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Persist filters/sort/page size so the page remembers how you left it
  useEffect(() => {
    localStorage.setItem(CALENDAR_ADMIN_FILTERS_KEY, JSON.stringify({
      searchTerm,
      filterType,
      filterVisibility,
      sortBy,
      sortOrder,
      pageSize,
    }));
  }, [searchTerm, filterType, filterVisibility, sortBy, sortOrder, pageSize]);

  // Update page title
  usePageTitle('Calendar Admin');

  // State for creating new calendar
  const [newCalendarName, setNewCalendarName] = useState('');
  const [newCalendarDescription, setNewCalendarDescription] = useState('');
  const [newCalendarType, setNewCalendarType] = useState('BUSINESS');
  const [newCalendarVisibility, setNewCalendarVisibility] = useState('PRIVATE');
  const [newCalendarColor, setNewCalendarColor] = useState('#4A90E2');
  const [newCalendarTimezone, setNewCalendarTimezone] = useState('Australia/Sydney');

  // State for editing calendar
  const [editCalendarName, setEditCalendarName] = useState('');
  const [editCalendarDescription, setEditCalendarDescription] = useState('');
  const [editCalendarType, setEditCalendarType] = useState('');
  const [editCalendarVisibility, setEditCalendarVisibility] = useState('');
  const [editCalendarColor, setEditCalendarColor] = useState('');

  // GraphQL queries
  const { data: calendarsData, loading: calendarsLoading, error: calendarsError, refetch: refetchCalendars } = useQuery(GET_ALL_CALENDARS);
  const { refetch: refetchExternalCalendars } = useQuery(GET_EXTERNAL_CALENDARS);
  const { data: clientsData, loading: clientsLoading } = useQuery(GET_CLIENTS);

  // Debug logging
  if (calendarsError) {
    console.error('Error fetching calendars:', calendarsError);
  }

  // Helper function to get owner details
  const getOwnerDetails = (ownerId: string) => {
    if (!clientsData?.clients) return null;
    return clientsData.clients.find((client: any) => client.id === ownerId);
  };


  // GraphQL mutations
  const [createCalendar] = useMutation(CREATE_CALENDAR, {
    onCompleted: (data) => {
      toast({
        title: 'Calendar created successfully',
        description: `Created ${data.createCalendar.name}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      refetchCalendars();
      onCreateClose();
      // Reset form
      setNewCalendarName('');
      setNewCalendarDescription('');
      setNewCalendarType('BUSINESS');
      setNewCalendarVisibility('PRIVATE');
      setNewCalendarColor('#4A90E2');
      setNewCalendarTimezone('Australia/Sydney');
    },
    onError: (error) => {
      toast({
        title: 'Error creating calendar',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const [updateCalendar] = useMutation(UPDATE_CALENDAR, {
    onCompleted: () => {
      toast({
        title: 'Calendar updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      refetchCalendars();
      onEditClose();
    },
    onError: (error) => {
      toast({
        title: 'Error updating calendar',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const [deleteCalendar] = useMutation(DELETE_CALENDAR, {
    onCompleted: () => {
      toast({
        title: 'Calendar deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      refetchCalendars();
    },
    onError: (error) => {
      toast({
        title: 'Error deleting calendar',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Accepted-email management removed — email→calendar routing is managed via the
  // Email module (EmailAddress.linkedCalendars); the old accepted-email mutations no
  // longer exist in the backend schema.

  const [transferOwnership] = useMutation(TRANSFER_OWNERSHIP, {
    onCompleted: () => {
      toast({
        title: 'Ownership transferred successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      refetchCalendars();
      onTransferClose();
      setNewOwnerId('');
    },
    onError: (error) => {
      toast({
        title: 'Error transferring ownership',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const cardGradientBg = getColor("background.cardGradient", colorMode);
  const cardBorder = getColor("border.darkCard", colorMode);
  const textColor = getColor(colorMode === 'light' ? "text.primary" : "text.primaryDark", colorMode);
  const mutedTextColor = getColor(colorMode === 'light' ? "text.muted" : "text.mutedDark", colorMode);
  const labelColor = getColor(colorMode === 'light' ? "text.secondary" : "text.secondaryDark", colorMode);

  const calendars = calendarsData?.calendars || [];

  // Get enum values from generated types
  const calendarTypes = Object.entries(CalendarType).map(([key, value]) => ({
    name: value,
    label: key.replace(/([A-Z])/g, ' $1').trim(), // Convert "SharedExternal" to "Shared External"
  }));

  const calendarVisibilities = Object.entries(CalendarVisibility).map(([key, value]) => ({
    name: value,
    label: key,
  }));

  // Debug logging
  console.log('Calendars data:', {
    calendarsData,
    calendars,
    count: calendars.length,
    loading: calendarsLoading,
    error: calendarsError,
    calendarTypes,
    calendarVisibilities
  });

  // Filter calendars based on (debounced) search and filters.
  // Search covers name, description, and the owner's name/email.
  const filteredCalendars = calendars.filter((calendar: any) => {
    const search = debouncedSearch.trim().toLowerCase();
    const owner = calendar.responsibleOwnerId ? getOwnerDetails(calendar.responsibleOwnerId) : null;
    const ownerText = owner
      ? `${owner.fName || ''} ${owner.lName || ''} ${owner.email || ''}`.toLowerCase()
      : '';
    const matchesSearch = !search ||
      calendar.name.toLowerCase().includes(search) ||
      calendar.description?.toLowerCase().includes(search) ||
      ownerText.includes(search);
    const matchesType = filterType === 'all' || calendar.type === filterType;
    const matchesVisibility = filterVisibility === 'all' || calendar.visibility === filterVisibility;

    return matchesSearch && matchesType && matchesVisibility;
  });

  // Client-side sort over the filtered list (all data is already loaded)
  const sortValue = (calendar: any): string | number => {
    switch (sortBy) {
      case 'name': return (calendar.name || '').toLowerCase();
      case 'type': return calendar.type || '';
      case 'visibility': return calendar.visibility || '';
      case 'owner': {
        const owner = calendar.responsibleOwnerId ? getOwnerDetails(calendar.responsibleOwnerId) : null;
        return owner ? `${owner.fName || ''} ${owner.lName || ''}`.trim().toLowerCase() : '';
      }
      case 'events': return calendar.totalEvents || 0;
      case 'createdAt': return calendar.createdAt ? new Date(calendar.createdAt).getTime() : 0;
      default: return '';
    }
  };

  const sortedCalendars = [...filteredCalendars].sort((a: any, b: any) => {
    const aVal = sortValue(a);
    const bVal = sortValue(b);
    let cmp = 0;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      cmp = aVal - bVal;
    } else {
      cmp = String(aVal).localeCompare(String(bVal));
    }
    return sortOrder === 'asc' ? cmp : -cmp;
  });

  // Toggle a sort column (asc <-> desc), or switch to it ascending
  const toggleSort = (field: CalendarSortField) => {
    if (sortBy === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Client-side pagination (table + cards share the same page slice)
  const totalFiltered = sortedCalendars.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedCalendars = sortedCalendars.slice((safePage - 1) * pageSize, safePage * pageSize);
  const showingFrom = totalFiltered === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const showingTo = Math.min(safePage * pageSize, totalFiltered);

  const handleCreateCalendar = async () => {
    if (!newCalendarName.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a calendar name',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    await createCalendar({
      variables: {
        input: {
          name: newCalendarName,
          description: newCalendarDescription || undefined,
          type: newCalendarType,
          visibility: newCalendarVisibility,
          color: newCalendarColor,
          settings: {
            timezone: newCalendarTimezone,
            emailNotifications: true,
            smsNotifications: false,
            defaultReminderMinutes: 15
          }
        },
      },
    });
  };

  const handleUpdateCalendar = async () => {
    if (!selectedCalendar) return;

    await updateCalendar({
      variables: {
        id: selectedCalendar.id,
        input: {
          name: editCalendarName,
          description: editCalendarDescription || undefined,
          type: editCalendarType,
          visibility: editCalendarVisibility,
          color: editCalendarColor,
        },
      },
    });
  };

  const handleDeleteCalendar = async (calendarId: string) => {
    if (window.confirm('Are you sure you want to delete this calendar? This action cannot be undone.')) {
      await deleteCalendar({
        variables: { id: calendarId },
      });
    }
  };

  const openEditModal = (calendar: any) => {
    setSelectedCalendar(calendar);
    setEditCalendarName(calendar.name);
    setEditCalendarDescription(calendar.description || '');
    setEditCalendarType(calendar.type);
    setEditCalendarVisibility(calendar.visibility);
    setEditCalendarColor(calendar.color || '#4A90E2');
    onEditOpen();
  };

  const openTransferModal = (calendar: any) => {
    setSelectedCalendar(calendar);
    setNewOwnerId('');
    onTransferOpen();
  };

  const handleTransferOwnership = async () => {
    if (!selectedCalendar) return;

    await transferOwnership({
      variables: {
        calendarId: selectedCalendar.id,
        newOwnerId: newOwnerId || undefined
      }
    });
  };

  const handleCopyToClipboard = (text: string, label: string = 'Text') => {
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: `${label} copied to clipboard`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    }).catch(() => {
      toast({
        title: 'Failed to copy',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    });
  };

  const getCalendarTypeColor = (type: string) => {
    switch (type) {
      case 'BUSINESS': return 'blue';
      case 'EMAIL': return 'green';
      case 'PERSONAL': return 'purple';
      case 'COMPANY': return 'orange';
      case 'EMPLOYEE': return 'cyan';
      case 'RESOURCE': return 'pink';
      case 'PROJECT': return 'yellow';
      default: return 'gray';
    }
  };

  const getVisibilityBadgeColor = (visibility: string) => {
    switch (visibility) {
      case 'PUBLIC': return 'green';
      case 'PRIVATE': return 'red';
      case 'SHARED': return 'blue';
      case 'COMPANY': return 'orange';
      case 'TEAM': return 'purple';
      default: return 'gray';
    }
  };

  // Toggle calendar selection
  const toggleCalendarSelection = (calendarId: string) => {
    setSelectedCalendarIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(calendarId)) {
        newSet.delete(calendarId);
      } else {
        newSet.add(calendarId);
      }
      return newSet;
    });
  };

  // Select/Deselect all calendars on the current page
  const allOnPageSelected = paginatedCalendars.length > 0 &&
    paginatedCalendars.every((c: any) => selectedCalendarIds.has(c.id));
  const someOnPageSelected = paginatedCalendars.some((c: any) => selectedCalendarIds.has(c.id));
  const toggleAllCalendars = () => {
    setSelectedCalendarIds(prev => {
      const newSet = new Set(prev);
      if (allOnPageSelected) {
        paginatedCalendars.forEach((c: any) => newSet.delete(c.id));
      } else {
        paginatedCalendars.forEach((c: any) => newSet.add(c.id));
      }
      return newSet;
    });
  };

  // Open the Share modal for a calendar
  const openShareModal = (calendar: any) => {
    setShareCalendar(calendar);
    onShareOpen();
  };

  // Open selected calendars in new tab
  const openMultipleCalendars = () => {
    if (selectedCalendarIds.size === 0) {
      toast({
        title: 'No calendars selected',
        description: 'Please select at least one calendar to view',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const calendarIds = Array.from(selectedCalendarIds).join(',');
    const url = `/calendars/view?calendars=${calendarIds}`;
    window.open(url, '_blank');

    // Clear selection after opening
    setSelectedCalendarIds(new Set());
  };

  if (calendarsLoading || clientsLoading) {
    return (
      <Box p={6} display="flex" alignItems="center" justifyContent="center" minH="50vh">
            <FloatingCalendarNav />
        <Spinner size="xl" color={textColor} />
      </Box>
    );
  }

  if (calendarsError) {
    return (
      <Box p={6}>
        <FloatingCalendarNav />
        <Card bg={cardGradientBg} border="1px solid" borderColor={cardBorder}>
          <CardBody>
            <VStack spacing={4}>
              <Text color="red.400" fontSize="lg">Error loading calendars</Text>
              <Text color={textColor} fontSize="sm">{calendarsError.message}</Text>
              <Tooltip label="Reload the calendar list from the server" hasArrow openDelay={300}>
                <Button onClick={() => refetchCalendars()} colorScheme="blue">
                  Try Again
                </Button>
              </Tooltip>
            </VStack>
          </CardBody>
        </Card>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }}>
        <FloatingCalendarNav />
        <VStack spacing={{ base: 4, md: 6 }} align="stretch" maxW={{ base: "100%", lg: "none" }}>
          {/* Header */}
          <Box>
            <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" color={textColor} mb={2}>
              📅 Calendar Management
            </Text>
            <Text color={mutedTextColor} fontSize={{ base: "md", md: "lg" }} lineHeight="1.6">
              Manage all business calendars, configure sharing settings, and monitor calendar usage across the organization.
            </Text>
          </Box>

          {/* Stats Cards */}
          <Stack spacing={{ base: 2, md: 4 }} direction={{ base: "column", md: "row" }} align="stretch">
            <Card
              bg={cardGradientBg}
              backdropFilter="blur(10px)"
              boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
              border="1px solid"
              borderColor={cardBorder}
              flex={1}
            >
              <CardBody p={{ base: 4, md: 6 }}>
                <HStack justify="space-between">
                  <VStack align="start" spacing={1} flex="1">
                    <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">Total Calendars</Text>
                    <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color={textColor}>
                      {calendars.length}
                    </Text>
                    <Text fontSize="2xs" color="gray.400" display={{ base: "none", md: "block" }}>
                      Active calendars in system
                    </Text>
                  </VStack>
                  <Box p={{ base: 2, md: 3 }} borderRadius="lg" bg="blue.500" color="white">
                    <FiCalendar size={24} />
                  </Box>
                </HStack>
              </CardBody>
            </Card>

            <Card
              bg={cardGradientBg}
              backdropFilter="blur(10px)"
              boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
              border="1px solid"
              borderColor={cardBorder}
              flex={1}
            >
              <CardBody p={{ base: 4, md: 6 }}>
                <HStack justify="space-between">
                  <VStack align="start" spacing={1} flex="1">
                    <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">Total Events</Text>
                    <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color={textColor}>
                      {calendars.reduce((sum: number, cal: any) => sum + (cal.totalEvents || 0), 0)}
                    </Text>
                    <Text fontSize="2xs" color="gray.400" display={{ base: "none", md: "block" }}>
                      Across all calendars
                    </Text>
                  </VStack>
                  <Box p={{ base: 2, md: 3 }} borderRadius="lg" bg="green.500" color="white">
                    <FiCalendar size={24} />
                  </Box>
                </HStack>
              </CardBody>
            </Card>

            <Card
              bg={cardGradientBg}
              backdropFilter="blur(10px)"
              boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
              border="1px solid"
              borderColor={cardBorder}
              flex={1}
            >
              <CardBody p={{ base: 4, md: 6 }}>
                <HStack justify="space-between">
                  <VStack align="start" spacing={1} flex="1">
                    <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">Public Calendars</Text>
                    <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color={textColor}>
                      {calendars.filter((c: any) => c.visibility === 'PUBLIC').length}
                    </Text>
                    <Text fontSize="2xs" color="gray.400" display={{ base: "none", md: "block" }}>
                      Visible to all users
                    </Text>
                  </VStack>
                  <Box p={{ base: 2, md: 3 }} borderRadius="lg" bg="purple.500" color="white">
                    <FiEye size={24} />
                  </Box>
                </HStack>
              </CardBody>
            </Card>
          </Stack>

          {/* Filters */}
          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            border="1px solid"
            borderColor={cardBorder}
          >
            <CardBody p={{ base: 4, md: 6 }}>
              <VStack spacing={4} align="stretch">
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.500" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search by calendar name, description, or owner..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    bg="whiteAlpha.100"
                    border="1px solid"
                    borderColor={cardBorder}
                    color={textColor}
                    _placeholder={{ color: "gray.500" }}
                  />
                </InputGroup>
                <Stack direction={{ base: "column", lg: "row" }} spacing={3} justifyContent="space-between">
                  <HStack spacing={3} flex="1">
                    <Tooltip label="Show only calendars of one type" hasArrow openDelay={300}>
                      <Select
                        value={filterType}
                        onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }}
                        bg="whiteAlpha.100"
                        border="1px solid"
                        borderColor={cardBorder}
                        color={textColor}
                        flex={1}
                      >
                        <option value="all">All Types</option>
                        {calendarTypes.map((type) => (
                          <option key={type.name} value={type.name}>
                            {type.label}
                          </option>
                        ))}
                      </Select>
                    </Tooltip>
                    <Tooltip label="Show only calendars with one visibility setting" hasArrow openDelay={300}>
                      <Select
                        value={filterVisibility}
                        onChange={(e) => { setFilterVisibility(e.target.value); setCurrentPage(1); }}
                        bg="whiteAlpha.100"
                        border="1px solid"
                        borderColor={cardBorder}
                        color={textColor}
                        flex={1}
                      >
                        <option value="all">All Visibility</option>
                        {calendarVisibilities.map((visibility) => (
                          <option key={visibility.name} value={visibility.name}>
                            {visibility.label}
                          </option>
                        ))}
                      </Select>
                    </Tooltip>
                  </HStack>
                  <HStack spacing={2} flexWrap="wrap" rowGap={2}>
                    {selectedCalendarIds.size > 0 && (
                      <Tooltip label="Open the ticked calendars together in one combined view" hasArrow openDelay={300}>
                        <Button
                          leftIcon={<FiEye />}
                          colorScheme="purple"
                          onClick={openMultipleCalendars}
                          size="md"
                          variant="outline"
                        >
                          View {selectedCalendarIds.size} Calendar{selectedCalendarIds.size !== 1 ? 's' : ''}
                        </Button>
                      </Tooltip>
                    )}
                    <Tooltip label="Create a brand new calendar" hasArrow openDelay={300}>
                      <Button
                        leftIcon={<FiPlus />}
                        colorScheme="blue"
                        onClick={onCreateOpen}
                        size="md"
                      >
                        Create Calendar
                      </Button>
                    </Tooltip>
                    <Tooltip label="Connect a calendar from Google or Outlook" hasArrow openDelay={300}>
                      <Button
                        leftIcon={<FiShare2 />}
                        colorScheme="green"
                        onClick={onExternalOpen}
                        size="md"
                        variant="outline"
                      >
                        Add External Calendar
                      </Button>
                    </Tooltip>
                  </HStack>
                </Stack>
              </VStack>
            </CardBody>
          </Card>

          {/* Calendars Table */}
          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            border="1px solid"
            borderColor={cardBorder}
          >
            <CardBody p={{ base: 2, md: 6 }}>
              <TableContainer>
                <Table variant="simple" size={{ base: "sm", md: "md" }}>
                  <Thead>
                    <Tr>
                      <Th color={labelColor} width="50px">
                        <Checkbox
                          isChecked={allOnPageSelected}
                          isIndeterminate={someOnPageSelected && !allOnPageSelected}
                          onChange={toggleAllCalendars}
                          colorScheme="purple"
                        />
                      </Th>
                      {([
                        { field: 'name' as CalendarSortField, label: 'Name' },
                        { field: 'type' as CalendarSortField, label: 'Type' },
                        { field: 'visibility' as CalendarSortField, label: 'Visibility' },
                        { field: 'owner' as CalendarSortField, label: 'Owner' },
                      ]).map(({ field, label }) => (
                        <Th
                          key={field}
                          color={labelColor}
                          cursor="pointer"
                          userSelect="none"
                          onClick={() => toggleSort(field)}
                        >
                          <Tooltip label={`Sort by ${label.toLowerCase()} — click again to reverse the order`} placement="top" hasArrow openDelay={300}>
                            <HStack spacing={1} display="inline-flex">
                              <Text>{label}</Text>
                              {sortBy === field && (
                                sortOrder === 'asc'
                                  ? <TriangleUpIcon boxSize={2.5} />
                                  : <TriangleDownIcon boxSize={2.5} />
                              )}
                            </HStack>
                          </Tooltip>
                        </Th>
                      ))}
                      <Th color={labelColor}>
                        <Text>Shared From</Text>
                      </Th>
                      {([
                        { field: 'events' as CalendarSortField, label: 'Events' },
                        { field: 'createdAt' as CalendarSortField, label: 'Created' },
                      ]).map(({ field, label }) => (
                        <Th
                          key={field}
                          color={labelColor}
                          cursor="pointer"
                          userSelect="none"
                          onClick={() => toggleSort(field)}
                        >
                          <Tooltip label={`Sort by ${label.toLowerCase()} — click again to reverse the order`} placement="top" hasArrow openDelay={300}>
                            <HStack spacing={1} display="inline-flex">
                              <Text>{label}</Text>
                              {sortBy === field && (
                                sortOrder === 'asc'
                                  ? <TriangleUpIcon boxSize={2.5} />
                                  : <TriangleDownIcon boxSize={2.5} />
                              )}
                            </HStack>
                          </Tooltip>
                        </Th>
                      ))}
                      <Th color={labelColor}>
                        <Text>Actions</Text>
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paginatedCalendars.map((calendar: any) => (
                      <Tr key={calendar.id}>
                        <Td width="50px">
                          <Checkbox
                            isChecked={selectedCalendarIds.has(calendar.id)}
                            onChange={() => toggleCalendarSelection(calendar.id)}
                            colorScheme="purple"
                          />
                        </Td>
                        <Td>
                          <HStack spacing={2}>
                            <Box
                              w={3}
                              h={3}
                              borderRadius="full"
                              bg={calendar.color || '#4A90E2'}
                            />
                            <VStack align="start" spacing={0}>
                              <Text color={textColor} fontWeight="medium">
                                {calendar.name}
                              </Text>
                              {calendar.description && (
                                <Text fontSize="xs" color={mutedTextColor} noOfLines={1}>
                                  {calendar.description}
                                </Text>
                              )}
                            </VStack>
                          </HStack>
                        </Td>
                        <Td>
                          <Badge colorScheme={getCalendarTypeColor(calendar.type)}>
                            {calendar.type}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={getVisibilityBadgeColor(calendar.visibility)}>
                            {calendar.visibility}
                          </Badge>
                        </Td>
                        <Td>
                          {(() => {
                            const ownerDetails = calendar.responsibleOwnerId ? getOwnerDetails(calendar.responsibleOwnerId) : null;
                            return (
                              <HStack spacing={2}>
                                {!calendar.responsibleOwnerId ? (
                                  <Box
                                    p={2}
                                    bg="whiteAlpha.50"
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor={cardBorder}
                                    flex="1"
                                  >
                                    <Text color={mutedTextColor} fontSize="sm" fontStyle="italic">
                                      No owner assigned
                                    </Text>
                                  </Box>
                                ) : ownerDetails ? (
                                  <Box
                                    p={2}
                                    bg="whiteAlpha.50"
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor={cardBorder}
                                    _hover={{ bg: "whiteAlpha.100" }}
                                    flex="1"
                                  >
                                    <VStack align="start" spacing={1}>
                                      {/* Name */}
                                      <HStack spacing={2}>
                                        <FiUser size={12} color={mutedTextColor} />
                                        <Text color={textColor} fontSize="sm" fontWeight="medium">
                                          {ownerDetails.fName} {ownerDetails.lName}
                                        </Text>
                                        <Badge colorScheme="green" size="sm">Owner</Badge>
                                      </HStack>

                                      {/* Email */}
                                      {ownerDetails.email && (
                                        <HStack spacing={2}>
                                          <FiMail size={12} color={mutedTextColor} />
                                          <Text color={mutedTextColor} fontSize="xs" noOfLines={1}>
                                            {ownerDetails.email}
                                          </Text>
                                        </HStack>
                                      )}

                                      {/* Client ID with copy */}
                                      <HStack spacing={1}>
                                        <Text color="gray.500" fontSize="xs">
                                          ID: {calendar.responsibleOwnerId}
                                        </Text>
                                        <Tooltip label="Copy this client ID to your clipboard" hasArrow openDelay={300}>
                                          <IconButton
                                            aria-label="Copy client ID"
                                            icon={<FiCopy />}
                                            size="xs"
                                            variant="ghost"
                                            color={mutedTextColor}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleCopyToClipboard(calendar.responsibleOwnerId, 'Client ID');
                                            }}
                                            _hover={{ color: textColor }}
                                          />
                                        </Tooltip>
                                      </HStack>
                                    </VStack>
                                  </Box>
                                ) : (
                                  <HStack spacing={1} flex="1">
                                    <Text color={textColor} fontSize="sm" noOfLines={1}>
                                      {calendar.responsibleOwnerId}
                                    </Text>
                                    <Tooltip label="Copy this owner ID to your clipboard" hasArrow openDelay={300}>
                                      <IconButton
                                        aria-label="Copy owner ID"
                                        icon={<FiCopy />}
                                        size="xs"
                                        variant="ghost"
                                        color={mutedTextColor}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCopyToClipboard(calendar.responsibleOwnerId, 'Owner ID');
                                        }}
                                        _hover={{ color: textColor }}
                                      />
                                    </Tooltip>
                                  </HStack>
                                )}
                                <Tooltip label="Hand this calendar over to a different owner" hasArrow openDelay={300}>
                                  <IconButton
                                    aria-label="Transfer ownership"
                                    icon={<FiUserCheck />}
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="orange"
                                    onClick={() => openTransferModal(calendar)}
                                  />
                                </Tooltip>
                              </HStack>
                            );
                          })()}
                        </Td>
                        <Td>
                          {calendar.sharedFromEmail ? (
                            <Text color={textColor} fontSize="sm" noOfLines={1}>
                              {calendar.sharedFromEmail}
                            </Text>
                          ) : (
                            <Text color={mutedTextColor} fontSize="sm">
                              -
                            </Text>
                          )}
                        </Td>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text color={textColor} fontSize="sm">
                              {calendar.totalEvents || 0} total
                            </Text>
                            <Text color={mutedTextColor} fontSize="xs">
                              {calendar.upcomingEvents || 0} upcoming
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <Text color={mutedTextColor} fontSize="xs">
                            {format(new Date(calendar.createdAt), 'MMM dd, yyyy')}
                          </Text>
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <Tooltip label="Open this calendar in a new tab" hasArrow openDelay={300}>
                              <IconButton
                                aria-label="View calendar"
                                icon={<FiEye />}
                                size="sm"
                                variant="ghost"
                                color={textColor}
                                onClick={() => window.open(`/calendars/${calendar.id}/view`, '_blank')}
                              />
                            </Tooltip>
                            <Tooltip label="Change this calendar's name, type, or colour" hasArrow openDelay={300}>
                              <IconButton
                                aria-label="Edit calendar"
                                icon={<FiEdit />}
                                size="sm"
                                variant="ghost"
                                color={textColor}
                                onClick={() => openEditModal(calendar)}
                              />
                            </Tooltip>
                            <Tooltip label="Share this calendar with other people — choose who can view or edit it" hasArrow openDelay={300}>
                              <IconButton
                                aria-label="Share calendar"
                                icon={<FiShare2 />}
                                size="sm"
                                variant="ghost"
                                colorScheme="green"
                                onClick={() => openShareModal(calendar)}
                              />
                            </Tooltip>
                            <Tooltip label="Delete this calendar permanently — this cannot be undone" hasArrow openDelay={300}>
                              <IconButton
                                aria-label="Delete calendar"
                                icon={<FiTrash2 />}
                                size="sm"
                                variant="ghost"
                                color="red.400"
                                onClick={() => handleDeleteCalendar(calendar.id)}
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
              {totalFiltered === 0 && (
                <Box py={8} textAlign="center">
                  <Text color={mutedTextColor}>No calendars found</Text>
                </Box>
              )}

              {/* Pagination */}
              {totalFiltered > 0 && (
                <Stack
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                  align={{ base: "stretch", md: "center" }}
                  spacing={3}
                  mt={4}
                >
                  <HStack spacing={3}>
                    <Text color={mutedTextColor} fontSize="sm" whiteSpace="nowrap">
                      Showing {showingFrom}–{showingTo} of {totalFiltered}
                    </Text>
                    <Tooltip label="How many calendars to show on each page" hasArrow openDelay={300}>
                      <Select
                        value={pageSize}
                        onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                        size="sm"
                        width="130px"
                        bg="whiteAlpha.100"
                        border="1px solid"
                        borderColor={cardBorder}
                        color={textColor}
                      >
                        <option value={10}>10 per page</option>
                        <option value={25}>25 per page</option>
                        <option value={50}>50 per page</option>
                      </Select>
                    </Tooltip>
                  </HStack>
                  <HStack spacing={2}>
                    <Tooltip shouldWrapChildren label="Go back to the previous page of calendars" hasArrow openDelay={300}>
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<ChevronLeftIcon />}
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        isDisabled={safePage === 1}
                        color={textColor}
                      >
                        Previous
                      </Button>
                    </Tooltip>
                    <Text color={mutedTextColor} fontSize="sm" whiteSpace="nowrap">
                      Page {safePage} of {totalPages}
                    </Text>
                    <Tooltip shouldWrapChildren label="Go to the next page of calendars" hasArrow openDelay={300}>
                      <Button
                        size="sm"
                        variant="outline"
                        rightIcon={<ChevronRightIcon />}
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        isDisabled={safePage >= totalPages}
                        color={textColor}
                      >
                        Next
                      </Button>
                    </Tooltip>
                  </HStack>
                </Stack>
              )}
            </CardBody>
          </Card>
          {/* Card-based Calendar View */}
          <Box>
            <Text fontSize="xl" fontWeight="bold" color={textColor} mb={4}>
              📋 Calendar Cards View
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {paginatedCalendars.map((calendar: any) => {
                const ownerDetails = calendar.responsibleOwnerId ? getOwnerDetails(calendar.responsibleOwnerId) : null;
                const bookingUrl = calendar.bookingPageSlug
                  ? `${window.location.origin}/book/${calendar.bookingPageSlug}`
                  : null;

                return (
                  <Card
                    key={calendar.id}
                    bg={cardGradientBg}
                    backdropFilter="blur(10px)"
                    boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
                    border="1px solid"
                    borderColor={cardBorder}
                    transition="all 0.3s ease"
                    _hover={{
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 40px 0 rgba(0, 0, 0, 0.5)',
                      borderColor: 'blue.400'
                    }}
                  >
                    <CardBody>
                      <VStack align="stretch" spacing={4}>
                        {/* Header with color indicator and checkbox */}
                        <HStack justify="space-between" align="start">
                          {/* minW=0 on the flex column — external calendars imported from
                              Google arrive with a URL as their name; without it the
                              unbreakable string pushes the checkbox off-screen. */}
                          <HStack spacing={3} flex={1} minW={0}>
                            <Box
                              w={4}
                              h={4}
                              borderRadius="full"
                              bg={calendar.color || '#4A90E2'}
                              flexShrink={0}
                            />
                            <VStack align="start" spacing={0} flex={1} minW={0}>
                              <Text fontSize="lg" fontWeight="bold" color={textColor} noOfLines={1} wordBreak="break-all" w="100%">
                                {calendar.name}
                              </Text>
                              {calendar.description && (
                                <Text fontSize="sm" color={mutedTextColor} noOfLines={2}>
                                  {calendar.description}
                                </Text>
                              )}
                            </VStack>
                          </HStack>
                          <Checkbox
                            isChecked={selectedCalendarIds.has(calendar.id)}
                            onChange={() => toggleCalendarSelection(calendar.id)}
                            colorScheme="purple"
                          />
                        </HStack>

                        {/* Badges */}
                        <HStack spacing={2} flexWrap="wrap">
                          <Badge colorScheme={getCalendarTypeColor(calendar.type)}>
                            {calendar.type}
                          </Badge>
                          <Badge colorScheme={getVisibilityBadgeColor(calendar.visibility)}>
                            {calendar.visibility}
                          </Badge>
                          {calendar.totalEvents > 0 && (
                            <Badge colorScheme="green">
                              {calendar.totalEvents} events
                            </Badge>
                          )}
                        </HStack>

                        {/* Owner Info */}
                        {ownerDetails && (
                          <Box
                            p={2}
                            bg="whiteAlpha.50"
                            borderRadius="md"
                            border="1px solid"
                            borderColor={cardBorder}
                          >
                            <HStack spacing={2}>
                              <FiUser size={14} color={mutedTextColor} />
                              <Text fontSize="sm" color={textColor} fontWeight="medium">
                                {ownerDetails.fName} {ownerDetails.lName}
                              </Text>
                            </HStack>
                            {ownerDetails.email && (
                              <HStack spacing={2} mt={1}>
                                <FiMail size={12} color={mutedTextColor} />
                                <Text fontSize="xs" color={mutedTextColor} noOfLines={1}>
                                  {ownerDetails.email}
                                </Text>
                              </HStack>
                            )}
                          </Box>
                        )}

                        <Divider borderColor={cardBorder} />

                        {/* Client-Style Action Buttons */}
                        <VStack spacing={2} align="stretch">
                          {/* Row 1: Edit & Event Types */}
                          <HStack spacing={2}>
                            <Tooltip label="Change this calendar's name, type, or colour" hasArrow openDelay={300}>
                              <Button
                                size="sm"
                                leftIcon={<FiEdit />}
                                colorScheme="blue"
                                variant="outline"
                                flex={1}
                                onClick={() => navigate(`/calendars/${calendar.id}/edit`)}
                              >
                                Edit
                              </Button>
                            </Tooltip>
                            {calendar.allowPublicBooking && (
                              <Tooltip label="Set up the kinds of appointments people can book" hasArrow openDelay={300}>
                                <Button
                                  size="sm"
                                  leftIcon={<FiCalendar />}
                                  colorScheme="purple"
                                  variant="outline"
                                  flex={1}
                                  onClick={() => navigate(`/calendars/${calendar.id}/event-types`)}
                                >
                                  Event Types
                                </Button>
                              </Tooltip>
                            )}
                          </HStack>

                          {/* Row 2: Availability & Copy URL */}
                          <HStack spacing={2}>
                            {calendar.allowPublicBooking && (
                              <Tooltip label="Set the days and hours when people can book time" hasArrow openDelay={300}>
                                <Button
                                  size="sm"
                                  leftIcon={<FiClock />}
                                  colorScheme="green"
                                  variant="outline"
                                  flex={1}
                                  onClick={() => navigate(`/calendars/${calendar.id}/availability`)}
                                >
                                  Availability
                                </Button>
                              </Tooltip>
                            )}
                            {bookingUrl && (
                              <Tooltip label="Copy the public booking link so you can send it to someone" hasArrow openDelay={300}>
                                <Button
                                  size="sm"
                                  leftIcon={<FiCopy />}
                                  colorScheme="teal"
                                  variant="outline"
                                  flex={1}
                                  onClick={() => handleCopyToClipboard(bookingUrl, 'Booking URL')}
                                >
                                  Copy URL
                                </Button>
                              </Tooltip>
                            )}
                          </HStack>

                          {/* Row 3: Share */}
                          <Tooltip label="Share this calendar with other people — choose who can view or edit it" hasArrow openDelay={300}>
                            <Button
                              size="sm"
                              leftIcon={<FiShare2 />}
                              colorScheme="green"
                              variant="outline"
                              onClick={() => openShareModal(calendar)}
                            >
                              Share
                            </Button>
                          </Tooltip>

                          <Divider borderColor={cardBorder} />

                          {/* Row 4: Transfer & Delete */}
                          <HStack spacing={2}>
                            <Tooltip label="Hand this calendar over to a different owner" hasArrow openDelay={300}>
                              <Button
                                size="sm"
                                leftIcon={<FiUserCheck />}
                                colorScheme="yellow"
                                variant="outline"
                                flex={1}
                                onClick={() => openTransferModal(calendar)}
                              >
                                Transfer
                              </Button>
                            </Tooltip>
                            <Tooltip label="Delete this calendar permanently — this cannot be undone" hasArrow openDelay={300}>
                              <Button
                                size="sm"
                                leftIcon={<FiTrash2 />}
                                colorScheme="red"
                                variant="outline"
                                flex={1}
                                onClick={() => handleDeleteCalendar(calendar.id)}
                              >
                                Delete
                              </Button>
                            </Tooltip>
                          </HStack>
                        </VStack>
                      </VStack>
                    </CardBody>
                  </Card>
                );
              })}
            </SimpleGrid>

            {totalFiltered === 0 && (
              <Box py={8} textAlign="center">
                <Text color={mutedTextColor}>No calendars found</Text>
              </Box>
            )}
          </Box>


          {/* Create Calendar Modal */}
          <Modal isOpen={isCreateOpen} onClose={onCreateClose} size={{ base: "full", md: "lg" }}>
            <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
            <ModalContent
              bg={cardGradientBg}
              border="1px solid"
              borderColor={cardBorder}
              margin={{ base: 0, md: "auto" }}
              borderRadius={{ base: 0, md: "md" }}
            >
              <ModalHeader color={textColor} fontSize={{ base: "lg", md: "xl" }}>
                Create New Calendar
              </ModalHeader>
              <ModalCloseButton color={textColor} />
              <ModalBody pb={6}>
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Text color="gray.300" fontSize="sm" mb={2}>
                      Calendar Name *
                    </Text>
                    <Input
                      placeholder="e.g., Team Meetings, Project X Calendar"
                      value={newCalendarName}
                      onChange={(e) => setNewCalendarName(e.target.value)}
                      bg="whiteAlpha.100"
                      border="1px solid"
                      borderColor={cardBorder}
                      color={textColor}
                      _placeholder={{ color: "gray.500" }}
                    />
                  </Box>

                  <Box>
                    <Text color="gray.300" fontSize="sm" mb={2}>
                      Description (Optional)
                    </Text>
                    <Textarea
                      placeholder="Calendar description..."
                      value={newCalendarDescription}
                      onChange={(e) => setNewCalendarDescription(e.target.value)}
                      bg="whiteAlpha.100"
                      border="1px solid"
                      borderColor={cardBorder}
                      color={textColor}
                      _placeholder={{ color: "gray.500" }}
                      rows={3}
                    />
                  </Box>

                  <Box>
                    <Text color="gray.300" fontSize="sm" mb={2}>
                      Calendar Type
                    </Text>
                    <Select
                      value={newCalendarType}
                      onChange={(e) => setNewCalendarType(e.target.value)}
                      bg="whiteAlpha.100"
                      border="1px solid"
                      borderColor={cardBorder}
                      color={textColor}
                    >
                      {calendarTypes.map((type) => (
                        <option key={type.name} value={type.name}>
                          {type.label}
                        </option>
                      ))}
                    </Select>
                  </Box>

                  <Box>
                    <Text color="gray.300" fontSize="sm" mb={2}>
                      Visibility
                    </Text>
                    <Select
                      value={newCalendarVisibility}
                      onChange={(e) => setNewCalendarVisibility(e.target.value)}
                      bg="whiteAlpha.100"
                      border="1px solid"
                      borderColor={cardBorder}
                      color={textColor}
                    >
                      {calendarVisibilities.map((visibility) => (
                        <option key={visibility.name} value={visibility.name}>
                          {visibility.label}
                        </option>
                      ))}
                    </Select>
                  </Box>

                  <Box>
                    <Text color="gray.300" fontSize="sm" mb={2}>
                      Calendar Color
                    </Text>
                    <HStack spacing={2}>
                      <Input
                        type="color"
                        value={newCalendarColor}
                        onChange={(e) => setNewCalendarColor(e.target.value)}
                        bg="whiteAlpha.100"
                        border="1px solid"
                        borderColor={cardBorder}
                        w={16}
                        h={10}
                      />
                      <Input
                        value={newCalendarColor}
                        onChange={(e) => setNewCalendarColor(e.target.value)}
                        bg="whiteAlpha.100"
                        border="1px solid"
                        borderColor={cardBorder}
                        color={textColor}
                        flex={1}
                      />
                    </HStack>
                  </Box>

                  <Box>
                    <Text color="gray.300" fontSize="sm" mb={2}>
                      Timezone
                    </Text>
                    <Select
                      value={newCalendarTimezone}
                      onChange={(e) => setNewCalendarTimezone(e.target.value)}
                      bg="whiteAlpha.100"
                      border="1px solid"
                      borderColor={cardBorder}
                      color={textColor}
                    >
                      <option value="Australia/Sydney">Australia/Sydney</option>
                      <option value="Australia/Melbourne">Australia/Melbourne</option>
                      <option value="Australia/Brisbane">Australia/Brisbane</option>
                      <option value="Australia/Perth">Australia/Perth</option>
                      <option value="Australia/Adelaide">Australia/Adelaide</option>
                      <option value="UTC">UTC</option>
                    </Select>
                  </Box>

                  <Box bg="blue.900" p={3} borderRadius="md" border="1px solid" borderColor="blue.700">
                    <Text color="blue.200" fontSize="sm">
                      ℹ️ This will create a new calendar that can be shared with clients and used for scheduling events.
                    </Text>
                  </Box>

                  <HStack spacing={3} justify="flex-end" pt={2}>
                    <Tooltip label="Close without creating a calendar" hasArrow openDelay={300}>
                      <Button variant="ghost" onClick={onCreateClose} color={textColor}>
                        Cancel
                      </Button>
                    </Tooltip>
                    <Tooltip shouldWrapChildren label="Save and create this new calendar" hasArrow openDelay={300}>
                      <Button
                        colorScheme="blue"
                        onClick={handleCreateCalendar}
                        isDisabled={!newCalendarName.trim()}
                      >
                        Create Calendar
                      </Button>
                    </Tooltip>
                  </HStack>
                </VStack>
              </ModalBody>
            </ModalContent>
          </Modal>

          {/* Edit Calendar Modal */}
          <Modal isOpen={isEditOpen} onClose={onEditClose} size={{ base: "full", md: "lg" }}>
            <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
            <ModalContent
              bg={cardGradientBg}
              border="1px solid"
              borderColor={cardBorder}
              margin={{ base: 0, md: "auto" }}
              borderRadius={{ base: 0, md: "md" }}
            >
              <ModalHeader color={textColor} fontSize={{ base: "lg", md: "xl" }}>
                Edit Calendar
              </ModalHeader>
              <ModalCloseButton color={textColor} />
              <ModalBody pb={6}>
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Text color="gray.300" fontSize="sm" mb={2}>
                      Calendar Name *
                    </Text>
                    <Input
                      value={editCalendarName}
                      onChange={(e) => setEditCalendarName(e.target.value)}
                      bg="whiteAlpha.100"
                      border="1px solid"
                      borderColor={cardBorder}
                      color={textColor}
                      _placeholder={{ color: "gray.500" }}
                    />
                  </Box>

                  <Box>
                    <Text color="gray.300" fontSize="sm" mb={2}>
                      Description
                    </Text>
                    <Textarea
                      value={editCalendarDescription}
                      onChange={(e) => setEditCalendarDescription(e.target.value)}
                      bg="whiteAlpha.100"
                      border="1px solid"
                      borderColor={cardBorder}
                      color={textColor}
                      _placeholder={{ color: "gray.500" }}
                      rows={3}
                    />
                  </Box>

                  <Box>
                    <Text color="gray.300" fontSize="sm" mb={2}>
                      Calendar Type
                    </Text>
                    <Select
                      value={editCalendarType}
                      onChange={(e) => setEditCalendarType(e.target.value)}
                      bg="whiteAlpha.100"
                      border="1px solid"
                      borderColor={cardBorder}
                      color={textColor}
                    >
                      {calendarTypes.map((type) => (
                        <option key={type.name} value={type.name}>
                          {type.label}
                        </option>
                      ))}
                    </Select>
                  </Box>

                  <Box>
                    <Text color="gray.300" fontSize="sm" mb={2}>
                      Visibility
                    </Text>
                    <Select
                      value={editCalendarVisibility}
                      onChange={(e) => setEditCalendarVisibility(e.target.value)}
                      bg="whiteAlpha.100"
                      border="1px solid"
                      borderColor={cardBorder}
                      color={textColor}
                    >
                      {calendarVisibilities.map((visibility) => (
                        <option key={visibility.name} value={visibility.name}>
                          {visibility.label}
                        </option>
                      ))}
                    </Select>
                  </Box>

                  <Box>
                    <Text color="gray.300" fontSize="sm" mb={2}>
                      Calendar Color
                    </Text>
                    <HStack spacing={2}>
                      <Input
                        type="color"
                        value={editCalendarColor}
                        onChange={(e) => setEditCalendarColor(e.target.value)}
                        bg="whiteAlpha.100"
                        border="1px solid"
                        borderColor={cardBorder}
                        w={16}
                        h={10}
                      />
                      <Input
                        value={editCalendarColor}
                        onChange={(e) => setEditCalendarColor(e.target.value)}
                        bg="whiteAlpha.100"
                        border="1px solid"
                        borderColor={cardBorder}
                        color={textColor}
                        flex={1}
                      />
                    </HStack>
                  </Box>

                  <HStack spacing={3} justify="flex-end" pt={2}>
                    <Tooltip label="Close without saving your changes" hasArrow openDelay={300}>
                      <Button variant="ghost" onClick={onEditClose} color={textColor}>
                        Cancel
                      </Button>
                    </Tooltip>
                    <Tooltip shouldWrapChildren label="Save your changes to this calendar" hasArrow openDelay={300}>
                      <Button
                        colorScheme="blue"
                        onClick={handleUpdateCalendar}
                        isDisabled={!editCalendarName.trim()}
                      >
                        Update Calendar
                      </Button>
                    </Tooltip>
                  </HStack>
                </VStack>
              </ModalBody>
            </ModalContent>
          </Modal>

          {/* Calendar sharing is now managed via the Email module (Email Accounts Admin > Linked Calendars) */}

          {/* Transfer Ownership Modal */}
          <Modal isOpen={isTransferOpen} onClose={onTransferClose} size={{ base: "full", md: "lg" }}>
            <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
            <ModalContent
              bg={cardGradientBg}
              border="1px solid"
              borderColor={cardBorder}
              margin={{ base: 0, md: "auto" }}
              borderRadius={{ base: 0, md: "md" }}
            >
              <ModalHeader color={textColor} fontSize={{ base: "lg", md: "xl" }}>
                Transfer Calendar Ownership
                {selectedCalendar && (
                  <Text fontSize="sm" color={mutedTextColor} mt={1}>
                    {selectedCalendar.name}
                  </Text>
                )}
              </ModalHeader>
              <ModalCloseButton color={textColor} />
              <ModalBody pb={6}>
                <VStack spacing={4} align="stretch">
                  {/* Current Owner */}
                  <Box>
                    <Text color="gray.300" fontSize="sm" mb={2}>
                      Current Owner
                    </Text>
                    {(() => {
                      const currentOwner = selectedCalendar?.responsibleOwnerId && clientsData?.clients?.find(
                        (c: any) => c.id === selectedCalendar.responsibleOwnerId
                      );
                      return selectedCalendar && !selectedCalendar.responsibleOwnerId ? (
                        <Box p={3} bg="whiteAlpha.50" borderRadius="md" border="1px solid" borderColor={cardBorder}>
                          <Text color={mutedTextColor} fontSize="sm" fontStyle="italic">
                            No owner currently assigned
                          </Text>
                        </Box>
                      ) : currentOwner ? (
                        <Box p={3} bg="whiteAlpha.50" borderRadius="md" border="1px solid" borderColor={cardBorder}>
                          <VStack align="start" spacing={1}>
                            <HStack>
                              <FiUser size={14} color={textColor} />
                              <Text color={textColor} fontSize="sm" fontWeight="medium">
                                {currentOwner.fName} {currentOwner.lName}
                              </Text>
                            </HStack>
                            {currentOwner.email && (
                              <Text color={mutedTextColor} fontSize="xs">
                                {currentOwner.email}
                              </Text>
                            )}
                          </VStack>
                        </Box>
                      ) : (
                        <Text color={mutedTextColor} fontSize="sm">
                          ID: {selectedCalendar?.responsibleOwnerId}
                        </Text>
                      );
                    })()}
                  </Box>

                  {/* New Owner Selection */}
                  <Box>
                    <Text color="gray.300" fontSize="sm" mb={2}>
                      Select New Owner
                    </Text>
                    <Select
                      placeholder="Select a client or no owner..."
                      value={newOwnerId}
                      onChange={(e) => setNewOwnerId(e.target.value)}
                      bg="whiteAlpha.100"
                      border="1px solid"
                      borderColor={cardBorder}
                      color={textColor}
                    >
                      <option value="">No Owner (Unassigned)</option>
                      {clientsData?.clients
                        ?.filter((client: any) =>
                          // Don't show current owner
                          client.id !== selectedCalendar?.responsibleOwnerId
                        )
                        .map((client: any) => (
                          <option key={client.id} value={client.id}>
                            {client.fName} {client.lName} ({client.email})
                          </option>
                        ))}
                    </Select>
                  </Box>

                  {/* Warning Message */}
                  <Box bg="orange.900" p={3} borderRadius="md" border="1px solid" borderColor="orange.700">
                    <VStack align="start" spacing={2}>
                      <Text color="orange.200" fontSize="sm" fontWeight="medium">
                        ⚠️ Important Information
                      </Text>
                      <Text color="orange.200" fontSize="xs">
                        • The new owner will have full control over this calendar
                      </Text>
                      <Text color="orange.200" fontSize="xs">
                        • The current owner will retain full admin access as a shared user
                      </Text>
                      <Text color="orange.200" fontSize="xs">
                        • The new owner can remove anyone's access, including the previous owner
                      </Text>
                    </VStack>
                  </Box>

                  {/* Actions */}
                  <HStack spacing={3} justify="flex-end" pt={2}>
                    <Tooltip label="Close without changing the owner" hasArrow openDelay={300}>
                      <Button variant="ghost" onClick={onTransferClose} color={textColor}>
                        Cancel
                      </Button>
                    </Tooltip>
                    <Tooltip label="Confirm and hand this calendar to the selected owner" hasArrow openDelay={300}>
                      <Button
                        colorScheme="orange"
                        onClick={handleTransferOwnership}
                      >
                        Transfer Ownership
                      </Button>
                    </Tooltip>
                  </HStack>
                </VStack>
              </ModalBody>
            </ModalContent>
          </Modal>

          {/* Share Calendar Modal */}
          {shareCalendar && (
            <ShareCalendarModal
              isOpen={isShareOpen}
              onClose={() => {
                onShareClose();
                setShareCalendar(null);
              }}
              calendarId={shareCalendar.id}
              calendarName={shareCalendar.name}
              onChanged={() => refetchCalendars()}
            />
          )}

          {/* External Calendar Modal */}
          <ExternalCalendarModal
            isOpen={isExternalOpen}
            onClose={onExternalClose}
            onSuccess={() => {
              refetchCalendars();
              refetchExternalCalendars();
              onExternalClose();
            }}
          />
        </VStack>
        <PoweredBy />
    </Box>
  );
};

export default CalendarAccountsAdmin;
